import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);

    // Tell the dialog the content is ready so it can bind listeners
    document.getElementById('quick-add-dialog')
      ?.dispatchEvent(new CustomEvent('quick-add:content-ready', { bubbles: true }));
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #kind = null; // 'sample' | 'swatch' | null
  #qty = 1;

  connectedCallback() {
    super.connectedCallback();
    const options = { signal: this.#abortController.signal };

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, options);
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, options);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Bind after content is morphed in to handle custom ATC logic
    this.addEventListener('quick-add:content-ready', this.#initFromDom, options);

    // Keep kind in sync on variant changes for ATC label updates
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateForKind, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleVariantUpdateForKind = (/** @type {CustomEvent} */ event) => {
    const variant = event.detail?.data?.variant;
    this.#kind = this.#deriveKindFromVariant(variant) ?? this.#readKindFromDom();
    this.#updateAtcLabel();
  };

  // ---------------- Ported Swatch/Sample Logic ----------------

  #getEls() {
    const root = this;
    const button = root.querySelector('product-form-component button[name="add"]');
    const label = root.querySelector('product-form-component button[name="add"] .add-to-cart-text__content');
    const qtyIn = root.querySelector('quantity-selector-component input[name="quantity"], input[name="quantity"]');
    const qsc = root.querySelector('quantity-selector-component');
    const minus = root.querySelector('quantity-selector-component .quantity-minus');
    const plus = root.querySelector('quantity-selector-component .quantity-plus');
    return { button, label, qtyIn, qsc, minus, plus };
  }

  #cleanText(text) {
    return String(text || '').replace(/data-[\w-]+="[^"]*"/g, '').replace(/\s+/g, ' ').trim();
  }

  #format(number) {
    return Number(number).toLocaleString();
  }

  #deriveKindFromVariant(variant) {
    if (!variant) return null;
    const text = (variant.options || []).join(' ').toLowerCase();
    if (text.includes('sample')) return 'sample';
    if (text.includes('swatch')) return 'swatch';
    return null;
  }

  #readKindFromDom() {
    const radios = this.querySelectorAll('variant-picker input[type="radio"]:checked');
    const joined = [...radios].map(r => r.value?.toLowerCase?.() || '').join(' ');
    if (joined.includes('swatch')) return 'swatch';
    if (joined.includes('sample')) return 'sample';
    return null;
  }

  #initFromDom = () => {
    const { label, qtyIn, qsc, minus, plus } = this.#getEls();
    if (!label) return;

    const cleanDefault = this.#cleanText(label.getAttribute('data-default-text') || label.textContent);
    label.setAttribute('data-default-text', cleanDefault);

    this.#qty = Math.max(1, Number(qtyIn?.value || 1));
    this.#kind = this.#kind ?? this.#readKindFromDom();

    if (qtyIn) {
      qtyIn.addEventListener('input', this.#onQtyInput, { signal: this.#abortController.signal });
      qtyIn.addEventListener('change', this.#onQtyInput, { signal: this.#abortController.signal });
    }

    const clickRefresh = () => setTimeout(() => this.#readQtyAndUpdate(), 0);
    minus?.addEventListener('click', clickRefresh, { signal: this.#abortController.signal });
    plus?.addEventListener('click', clickRefresh, { signal: this.#abortController.signal });

    try {
      const evtName = window?.QuantitySelectorUpdateEvent?.eventName || 'quantity-selector:update';
      qsc?.addEventListener(evtName, (e) => {
        const value = Number(e.detail?.value);
        if (!Number.isNaN(value)) {
          this.#qty = Math.max(1, value);
          this.#updateAtcLabel();
        }
      }, { signal: this.#abortController.signal });
    } catch (_) {}

    this.#updateAtcLabel();
  };

  #onQtyInput = (event) => {
    const num = Number((/** @type {HTMLInputElement} */ event.target).value);
    this.#qty = Number.isFinite(num) && num > 0 ? num : 1;
    this.#updateAtcLabel();
  };

  #readQtyAndUpdate() {
    const { qtyIn } = this.#getEls();
    const num = Number(qtyIn?.value);
    this.#qty = Number.isFinite(num) && num > 0 ? num : 1;
    this.#updateAtcLabel();
  }

  #updateAtcLabel() {
    const { button, label } = this.#getEls();
    if (!button || !label) return;

    const qty = Math.max(1, Number(this.#qty) || 1);
    const isSwatch = this.#kind === 'swatch';
    const isSample = this.#kind === 'sample';

    if (isSwatch && qty > 1) {
      button.disabled = true;
      label.textContent = 'Swatches are limited to 1';
      button.classList.add('is-disabled-by-rule');
      return;
    }

    button.disabled = false;
    button.classList.remove('is-disabled-by-rule');

    if (isSample) {
      label.textContent = `Add ${this.#format(qty)} ${qty === 1 ? 'yard' : 'yards'} to cart`;
    } else if (isSwatch) {
      label.textContent = `Add 1 swatch to cart`;
    } else {
      label.textContent = label.getAttribute('data-default-text') || 'Add to cart';
    }
  }

  // ---------------- Existing New Logic ----------------

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (event.detail.data.html?.querySelector('.view-product-title a'));
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = /** @type {HTMLElement | null} */ (document.querySelector('#ResultsList [product-grid-view]'));
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}